import { useEffect, useRef, useState } from "react";
import { Input } from "@/design-system/components/ui";
import { userSearchApi } from "@/api/workTasks";
import type { UserSearchResultDto } from "@/api/workTasks";
import { UserSuggestionList } from "./UserSuggestionList";

const DEBOUNCE_MS = 250;
const MAX_RESULTS = 10;

export interface UserPickerProps {
  /** User id of the current selection, or <c>null</c> for unassigned. */
  value: string | null;
  /**
   * Display name to seed the input with on first render. Without this
   * the picker would show the raw user id (a leak) until the user
   * types — never acceptable per Tasks v1.1 § 3.4.
   */
  initialDisplayName?: string;
  onChange: (userId: string | null, displayName: string) => void;
  allowUnassigned?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  ariaLabel?: string;
}

/**
 * Tasks v1.1 § 3.3 — shared autocomplete picker used by the assignee
 * editor on the task detail sidebar and (indirectly, via
 * <c>UserSuggestionList</c>) by the @mention dropdown in the comment
 * composer.
 *
 * Owns: input + query state, 250ms debounced fetch against
 * <c>GET /api/users/search</c>, dropdown open/close, keyboard nav
 * (Up / Down / Enter / Escape), pinned "Unassigned" option.
 */
export function UserPicker({
  value,
  initialDisplayName,
  onChange,
  allowUnassigned,
  placeholder,
  autoFocus,
  ariaLabel,
}: UserPickerProps) {
  // The input's text is *display-only* — never the raw user id. We hold
  // the resolved id separately in `selectedId`.
  const [text, setText] = useState(initialDisplayName ?? "");
  const [selectedId, setSelectedId] = useState<string | null>(value);
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState<UserSearchResultDto[]>([]);
  const [highlighted, setHighlighted] = useState(0);
  const [loading, setLoading] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastQueryRef = useRef<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idPrefix = useRef(`up-${Math.random().toString(36).slice(2, 8)}`).current;

  // Sync down: if the parent commits a new value externally (e.g.
  // resets the picker after a Cancel), reset text + selectedId. Skip
  // when the incoming `value` already matches our internal
  // `selectedId` — that means the parent is echoing back the commit
  // we just fired via onChange, and overwriting `text` here would
  // clobber the display name we set inside `commit()` and snap the
  // input back to the stale `initialDisplayName`.
  useEffect(() => {
    if (value !== selectedId) {
      setSelectedId(value);
      setText(initialDisplayName ?? "");
    }
    // selectedId is intentionally not in the deps: this effect only
    // reacts to *external* prop changes. Local commits update
    // selectedId synchronously inside commit() so the next render
    // sees them; including selectedId here would create a feedback
    // loop on every commit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, initialDisplayName]);

  useEffect(() => {
    if (!open) return;
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [open]);

  // Click-outside dismiss.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const node = containerRef.current;
      if (!node) return;
      if (!node.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function scheduleSearch(query: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      lastQueryRef.current = query;
      setLoading(true);
      try {
        const results = await userSearchApi.search(query, MAX_RESULTS);
        // Discard out-of-order responses if a newer keystroke has
        // already kicked off a follow-up query.
        if (lastQueryRef.current === query) {
          setMatches(results);
          setHighlighted(allowUnassigned ? 0 : 0);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  }

  // pinnedOffset = 1 if "Unassigned" is rendered, else 0.
  const pinnedOffset = allowUnassigned ? 1 : 0;
  const totalRows = matches.length + pinnedOffset;

  function commit(id: string | null, displayName: string) {
    setSelectedId(id);
    setText(displayName);
    setOpen(false);
    onChange(id, displayName);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        type="text"
        autoFocus={autoFocus}
        value={text}
        placeholder={placeholder ?? "Search users…"}
        aria-label={ariaLabel ?? "User"}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? `${idPrefix}-list` : undefined}
        aria-activedescendant={
          open && totalRows > 0 ? `${idPrefix}-opt-${highlighted}` : undefined
        }
        role="combobox"
        onFocus={() => {
          // Open on focus and prime the dropdown with the empty-query
          // top-N so the user sees options before typing.
          setOpen(true);
          scheduleSearch("");
        }}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          setOpen(true);
          // Treat any keystroke as "I'm searching for a different user"
          // — clear the resolved id so a Save without a re-pick can be
          // detected by the parent.
          if (selectedId !== null) setSelectedId(null);
          scheduleSearch(next);
        }}
        onKeyDown={(e) => {
          if (!open) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
              scheduleSearch(text);
            }
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (totalRows > 0) {
              setHighlighted((i) => (i + 1) % totalRows);
            }
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (totalRows > 0) {
              setHighlighted((i) => (i - 1 + totalRows) % totalRows);
            }
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (allowUnassigned && highlighted === 0) {
              commit(null, "Unassigned");
            } else {
              const m = matches[highlighted - pinnedOffset];
              if (m) commit(m.id, m.displayName);
            }
          } else if (e.key === "Escape") {
            e.preventDefault();
            setOpen(false);
          }
        }}
      />
      {open && (
        <UserSuggestionList
          idPrefix={idPrefix}
          matches={matches}
          highlighted={highlighted}
          pinned={
            allowUnassigned
              ? { id: null, displayName: "Unassigned", secondary: "Clear assignee" }
              : undefined
          }
          onSelect={(id, name) => commit(id, name)}
        />
      )}
      {open && loading && matches.length === 0 && (
        <div
          aria-live="polite"
          className="absolute z-30 mt-space-1 w-72 px-space-3 py-space-2 rounded-md border border-border-light bg-white-pure shadow-lg text-label text-gray-mid"
        >
          Searching…
        </div>
      )}
    </div>
  );
}
